import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { LogInPaths } from '@bbkAdminComponents/log-in-flow/log-in-paths';
import * as Action from '@bbkAdminRedux/app/actions';
import { type ActiveUser } from '@bbkAdminRedux/app/reducers';
import { useSignInRedirect } from '@bbkAdminRedux/app/useSignInRedirect';
import { getActiveApp, getActiveCompany } from '@bbkAdminRedux/app/utils';
import { useAppDispatch } from '@bbkAdminRedux/redux_utils';
import { selectAuth } from '@bbkAdminRedux/rtkq/brightback-auth.slice';
import { clearSessionMetadata } from '@bbkAdminRedux/rtkq/clear-session-metadata';
import BrightbackSession from '@bbkAdminUtils/brightback-session';
import { hardRedirect } from '@bbkAdminUtils/browser-navigation';
import { type Maybe } from '@bbkAdminUtils/utility-types';

export const useAppInit = () => {
  const dispatch = useAppDispatch();
  const { url: signInRedirectUri } = useSignInRedirect();
  // Local non-secret session metadata. Its presence lets us detect a stale
  // session when the `/api/current_user` probe returns no user (the credential
  // itself is an unreadable HttpOnly cookie).
  //
  // Read through a ref so the returned callback keeps a stable identity. Sign-in
  // writes this metadata, and AuthRoot's boot probe effect depends on the
  // callback: a new identity re-runs that probe mid-sign-in, and the resulting
  // `appInit(user)` can hard-navigate away while the company-creation POSTs are
  // still in flight.
  const staleSession = useSelector(selectAuth);
  const staleSessionRef = useRef(staleSession);
  staleSessionRef.current = staleSession;

  return useCallback(
    (user: Maybe<ActiveUser>) => {
      if (user) {
        if (window.location.pathname !== LogInPaths.selector) {
          const company = getActiveCompany(user);
          if (company) {
            BrightbackSession.setActiveCompany(company, false);
            dispatch({
              type: Action.SET_ACTIVE_COMPANY,
              activeCompany: company,
            });
            const app = getActiveApp(company);
            dispatch({ type: Action.SET_ACTIVE_APP, activeApp: app });
            BrightbackSession.setActiveApp(app);
          } else {
            BrightbackSession.setActiveCompany(undefined);
          }
        }
        dispatch({ type: Action.DATA_LOADED });
      } else if (staleSessionRef.current) {
        // The probe returned no user but stale local session metadata is still
        // present: the server session is gone, so drop local state and bounce
        // to sign-in with a security notice. No CBIDP server logout is issued
        // for an already-ended session.
        BrightbackSession.setActiveCompany(undefined);
        BrightbackSession.setActiveApp(undefined);
        BrightbackSession.setAuthAlert('Please log in again for security');
        clearSessionMetadata(dispatch);
        hardRedirect(signInRedirectUri);
      }
    },
    [dispatch, signInRedirectUri]
  );
};
